import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listNotifications, markNotificationRead, markAllNotificationsRead,
} from "@/lib/social";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { supabase } from "@/lib/supabase";
import {
  Bell, Check, MessageSquare, UserPlus, CalendarPlus, ShieldCheck, X, ArrowRight, Hand, Sparkles,
} from "lucide-react";
import Avatar from "@/components/Avatar";

const ICONS = {
  rsvp_request:   UserPlus,
  rsvp_approved:  ShieldCheck,
  rsvp_rejected:  X,
  new_event:      CalendarPlus,
  new_follower:   UserPlus,
  new_message:    MessageSquare,
  event_reminder: Bell,
  nearby_wave:    Hand,
  nearby_mutual:  Sparkles,
};

function copyFor(n) {
  const actor = n.actor?.display_name || "Someone";
  const title = n.payload?.event_title || "an event";
  switch (n.type) {
    case "rsvp_request":   return [`${actor} ${n.payload?.auto_approved ? "joined" : "asked to join"}`, title];
    case "rsvp_approved":  return [`${actor} approved your RSVP`, title];
    case "rsvp_rejected":  return [`${actor} declined your RSVP`, title];
    case "new_event":      return [`${actor} posted a new event`, `${title}${n.payload?.city ? " · " + n.payload.city : ""}`];
    case "new_follower":   return [`${actor} followed you`, ""];
    case "new_message":    return [`${actor} sent you a message`, n.payload?.preview || ""];
    case "event_reminder": return [`Reminder`, title];
    case "nearby_wave":    return [`${actor} waved at you 👋`, title];
    case "nearby_mutual":  return [`You both waved — say hi to ${actor}`, title];
    default:               return ["Notification", ""];
  }
}

function timeAgo(iso) {
  const d = new Date(iso); const ms = Date.now() - d.getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const dy = Math.floor(h / 24);
  if (dy < 7) return `${dy}d`;
  return d.toLocaleDateString();
}

function targetFor(n) {
  if (n.type === "new_message") return n.actor?.id ? `/messages/${n.actor.id}` : "/messages";
  if (n.type === "nearby_mutual") return n.actor?.id ? `/messages/${n.actor.id}` : "/messages";
  if (n.type === "new_follower") return n.actor?.username
    ? `/u/${n.actor.username}`
    : (n.actor?.id ? `/u/${n.actor.id}` : "#");
  if (n.event_id && (n.type === "rsvp_request")) return `/events/${n.event_id}/manage`;
  if (n.event_id) return `/events/${n.event_id}`;
  return "#";
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const { refresh } = useRealtime();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user?.id) return;
    try {
      const data = await listNotifications(user.id);
      setItems(data);
    } catch (err) {
      console.error("[notifications] load failed", err);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // realtime: when notifications change for this user, reload the list
  useEffect(() => {
    if (!user?.id) return;
    const ch = supabase
      .channel(`notif-page-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => load()
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const onMarkAll = async () => {
    if (!user?.id) return;
    await markAllNotificationsRead(user.id);
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
    refresh();
  };

  const onItemClick = async (n) => {
    if (!n.read) {
      await markNotificationRead(n.id);
      setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, read: true } : x));
      refresh();
    }
  };

  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-20">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-ink">Inbox</span>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-2 leading-none">
              Notifications.
            </h1>
            {unread > 0 && (
              <p className="text-ink/60 mt-2 text-sm">{unread} unread</p>
            )}
          </div>
          {unread > 0 && (
            <button data-testid="mark-all-read" onClick={onMarkAll}
              className="border border-ink text-ink hover:bg-paper hover:text-ink px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
              <Check size={14} strokeWidth={2.5} /> Mark all read
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="border border-ink/15 h-20 bg-[#101010] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-ink/15 p-12 text-center">
            <Bell size={28} strokeWidth={1.5} className="mx-auto text-ink/30 mb-4" />
            <p className="font-display text-2xl text-ink">All caught up.</p>
            <p className="text-ink/60 mt-2">When something happens, you'll see it here.</p>
          </div>
        ) : (
          <div data-testid="notif-list" className="border border-ink/15 divide-y divide-border">
            {items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              const [line1, line2] = copyFor(n);
              const target = targetFor(n);
              return (
                <Link
                  key={n.id}
                  to={target}
                  onClick={() => onItemClick(n)}
                  data-testid={`notif-${n.id}`}
                  className={`flex items-start gap-4 px-4 md:px-5 py-4 transition-colors hover:bg-paper/5 ${
                    n.read ? "" : "bg-paper/[0.03]"
                  }`}
                >
                  {n.actor ? (
                    <div className="relative shrink-0">
                      <Avatar src={n.actor.avatar_url} name={n.actor.display_name} size="md" />
                      <span className={`absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center border bg-paper ${
                        n.read ? "border-ink/15 text-ink/50" : "border-neon text-ink"
                      }`}>
                        <Icon size={10} strokeWidth={2} />
                      </span>
                    </div>
                  ) : (
                    <div className={`w-9 h-9 shrink-0 border flex items-center justify-center transition-colors ${
                      n.read ? "border-ink/15 text-ink/50" : "border-neon text-ink"
                    }`}>
                      <Icon size={14} strokeWidth={1.8} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${n.read ? "text-ink/70" : "text-ink"}`}>
                      {line1}
                    </p>
                    {line2 && (
                      <p className="text-xs text-ink/50 mt-0.5 truncate">{line2}</p>
                    )}
                    <p className="text-[10px] uppercase tracking-widest text-ink/40 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="w-2 h-2 bg-neon shrink-0 mt-1.5" />}
                  <ArrowRight size={14} strokeWidth={2} className="text-ink/30 shrink-0 mt-1" />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
